import useDataset from "datasets/hooks/useDataset";
import { AlertCircle } from "lucide-react";
import { Skeleton } from "~/components/ui/skeleton";
import { DatasetDashboards } from "~/pages/manifold-nextgen/components/datasets/pages/DatasetDashboards";
import { DatasetFiles } from "~/pages/manifold-nextgen/components/datasets/pages/DatasetFiles";
import { DatasetOverview } from "~/pages/manifold-nextgen/components/datasets/pages/DatasetOverview";
import { SessionContextDatasetTables } from "./SessionContextDatasetTables";
import { SessionContextDetailHeader, SessionContextViewOption } from "./SessionContextDetailHeader";

const DATASET_VIEWS: SessionContextViewOption[] = [
  { value: "overview", label: "Overview" },
  { value: "dashboards", label: "Dashboards" },
  { value: "tables", label: "Tables" },
  { value: "files", label: "Files" },
];

interface SessionContextDatasetDetailProps {
  datasetId: string;
  view: string;
  onBack: () => void;
  onSetView: (view: string) => void;
}

export const SessionContextDatasetDetail = ({
  datasetId,
  view,
  onBack,
  onSetView,
}: SessionContextDatasetDetailProps) => {
  const { data: dataset, error, isLoading } = useDataset(datasetId);

  return (
    <div className="flex h-full flex-col overflow-hidden">
      <SessionContextDetailHeader
        entityName={dataset?.title ?? "…"}
        onBack={onBack}
        views={DATASET_VIEWS}
        currentView={view}
        onSetView={onSetView}
      />

      <div className="flex-1 overflow-y-auto p-4">
        {isLoading ? (
          <div className="flex flex-col gap-3" role="status" aria-label="Loading dataset">
            <Skeleton className="h-8 w-2/3 rounded-md" />
            <Skeleton className="h-32 rounded-lg" />
          </div>
        ) : error || !dataset ? (
          <div
            role="alert"
            className="flex flex-col items-center gap-3 py-16 text-center text-destructive"
          >
            <AlertCircle className="size-8" aria-hidden="true" />
            <p className="text-sm font-medium">Failed to load dataset</p>
          </div>
        ) : (
          <>
            {view === "overview" && <DatasetOverview dataset={dataset} sessionSidebarView />}
            {view === "dashboards" && <DatasetDashboards dataset={dataset} sessionSidebarView />}
            {view === "tables" && <SessionContextDatasetTables dataset={dataset} />}
            {view === "files" && <DatasetFiles dataset={dataset} sessionSidebarView />}
          </>
        )}
      </div>
    </div>
  );
};
